import React from 'react';
import {
  responsiveFontSizes,
  createTheme,
  Theme,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import { common } from '@material-ui/core/colors';
import { AlertProps } from '@material-ui/lab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

declare module '@material-ui/core/styles/props' {
  interface ComponentsPropsList {
    MuiAlert: AlertProps;
  }
}

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    tapeChart: {
      headerSpacingTop: string;
      rowHeight: number;
    };
    loadingIndicator: {
      dotSize: number;
      dotColor?: string;
    };
    // guest check-in accent (room number, finish button); kiosk keeps its blues when unset
    checkIn?: {
      accent?: string;
    };
  }

  // allow configuration using `createTheme`
  interface ThemeOptions {
    tapeChart?: {
      headerSpacingTop?: string;
      rowHeight?: number;
    };
    loadingIndicator?: {
      dotSize?: number;
      dotColor?: string;
    };
    checkIn?: {
      accent?: string;
    };
  }
}

export enum AppColors {
  LightBlue = '#00C1FF',
  Blue = '#054584',
  MediumBlue = '#133C65',
  DarkBlue = '#12263A',
  Red = '#BF2F2F',
  LighterGray = '#EFEFEF',
  LightGray = '#DEDEDE',
  Gray = '#828282',
  MediumGray = '#707070',
  DarkGray = '#12263A',
  White = '#FFFFFF',
  HeaderDivider = 'rgba(5,69,132,0.8)',
  RowDivider = 'rgba(5,69,132,0.4)',
  DarkGreen = '#228550',
  Green = '#2FBF71',
  TransparentDarkBlue = 'rgba(18,38,58,0.8)',
  TransparentDarkGreen = 'rgba(34,133,80,0.8)',
  TransparentRed = 'rgba(191,47,47,0.8)',
  DisabledHeaderItem = 'rgba(0,193,255,0.4)',
}

export enum GuestColors {
  Ironhide = '#1E1B18',
  Ironhide2 = '#2D2925',
  Ironhide3 = '#3D3833',
  Sandwash = '#F4F1EC',
  Sandwash2 = '#EAE6DF',
  SandwashSoft = '#FAF8F5',
  BurntClay = '#C4491D',
  BurntClayDark = '#A33B16',
  Sage = '#4E7262',
  Caliche = '#8A7D6E',
  TextMid = '#5A544D',
}

function responsiveTheme(theme: Theme): Theme {
  theme.overrides = {
    ...theme.overrides,
    MuiInputBase: {
      ...theme.overrides?.MuiInputBase,
      root: {},
    },
    MuiInputLabel: {
      ...theme.overrides?.MuiInputLabel,
      root: {},
    },
  };

  return responsiveFontSizes(theme);
}

export const guestServicesTheme = responsiveTheme(
  createTheme({
    palette: {
      background: {
        default: GuestColors.SandwashSoft,
        paper: common.white,
      },
      // primary = brand neutrals + field accent (labels/focus); secondary = CTA, as today
      primary: {
        main: GuestColors.Ironhide2,
        dark: GuestColors.Ironhide,
        light: GuestColors.Ironhide3,
        contrastText: GuestColors.Sandwash,
      },
      secondary: {
        main: GuestColors.BurntClay,
        dark: GuestColors.BurntClayDark,
        contrastText: common.white,
      },
      grey: {
        '300': GuestColors.Sandwash2,
        '500': GuestColors.Caliche,
        '800': GuestColors.Ironhide3,
      },
      text: {
        primary: GuestColors.Ironhide,
        secondary: GuestColors.TextMid,
      },
      action: {
        disabledBackground: GuestColors.Sandwash2,
      },
    },
    shape: {
      borderRadius: 8, // --radius-sm; Papers stay square via the MuiPaper prop below
    },
    tapeChart: {
      headerSpacingTop: '2rem',
      rowHeight: 48,
    },
    loadingIndicator: {
      dotSize: 10,
      dotColor: GuestColors.BurntClay,
    },
    checkIn: {
      accent: GuestColors.Sage,
    },
    typography: {
      fontFamily: 'aileron, "Helvetica Neue", Arial, sans-serif',
      h1: {
        fontFamily: 'prohibition, aileron, sans-serif',
        fontWeight: 700,
      },
      h2: {
        fontFamily: 'prohibition, aileron, sans-serif',
        fontWeight: 700,
      },
      h3: {
        fontFamily: 'prohibition, aileron, sans-serif',
        fontWeight: 700,
      },
      h6: {
        fontWeight: 700,
      },
      subtitle1: {
        fontWeight: 700,
      },
      subtitle2: {
        fontWeight: 700,
      },
      button: {
        fontWeight: 700,
      },
      overline: {
        letterSpacing: 'normal',
      },
    },
    mixins: {
      toolbar: {
        minHeight: 48,
      },
    },
    props: {
      MuiPaper: {
        square: true,
      },
      MuiAlert: {
        iconMapping: {
          success: <FontAwesomeIcon icon="check-circle" />,
          info: <FontAwesomeIcon icon="info-circle" />,
          warning: <FontAwesomeIcon icon="exclamation-circle" />,
          error: <FontAwesomeIcon icon="times-circle" />,
        },
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 960,
        lg: 1140,
        xl: 1920,
      },
    },
    zIndex: {
      mobileStepper: 1000,
      speedDial: 1050,
      appBar: 1299,
      drawer: 1298,
      modal: 1300,
      snackbar: 1400,
      tooltip: 1500,
    },
    overrides: {
      MuiContainer: {
        root: {
          paddingLeft: '0.75rem',
          paddingRight: '0.75rem',
          '@media (min-width:600px)': {
            paddingLeft: '0.5rem',
            paddingRight: '0.5rem',
          },
          '@media (min-width:960px)': {
            paddingLeft: '0',
            paddingRight: '0',
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          color: common.white,
          border: `1px solid ${common.white}`,
          borderRadius: '6px',
          '&::placeholder': {
            color: common.white,
          },
        },
      },
      MuiInputLabel: {
        outlined: {
          color: common.white,
        },
      },
      MuiInput: {
        underline: {
          '&.Mui-disabled:before': {
            borderBottomStyle: 'solid',
            borderBottomColor: 'rgba(148,148,148,0.5)',
          },
        },
      },
      MuiButton: {
        outlinedSecondary: {
          color: AppColors.Red,
          backgroundColor: 'transparent',
          borderColor: AppColors.Red,
          '&:hover': {
            borderColor: AppColors.Red + ' !important',
            backgroundColor: 'rgba(191,47,47,0.06) !important',
          },
        },
      },
    },
  })
);

export const webStyles = makeStyles(theme =>
  createStyles({
    listContentBlock: {
      margin: '2em 0 1em !important',
    },
  })
);
